import { useCallback, useEffect, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Edit from '@mui/icons-material/Edit';
import { LogSectionsList } from './LogSectionsList';
import { EmptyLogSectionsScreen } from './EmptyLogSectionsScreen';
import { FieldPanel, FormHeader, LineBreak, tooltipStyles } from '../../TemplateForm/styles';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle } from '@/theme';
import {
  costLogsTemplateFields,
  selectedCostLogsTemplate,
  selectedCostLogsTemplateId,
  selectedWorklogsTemplate,
  selectedWorklogsTemplateId,
  worklogsTemplateFields,
} from '../../state';
import {
  AllLogsTemplatesByTypeDocument,
  GetVersionsDocument,
  LogSection,
  SectionUnitsEnum,
  LogTemplateType,
  useUpdateLogsTemplateMutation,
  LogsTemplateDocument,
} from '@/__generated__/graphql';
import { removeTypename, useSnackBarMessage } from '../../utils';
import { generateMongoObjectId } from '@/utils/databaseHelpers';
import { HOST_TEMPLATE_DISPLAY } from '../../constants';

type KpiLogFormConfigProps = {
  structureId: string;
  hostLogTemplateType: LogTemplateType;
};

export const KpiLogFormConfig = ({ structureId, hostLogTemplateType }: KpiLogFormConfigProps) => {
  const worklogsTemplate = useAtomValue(selectedWorklogsTemplate);
  const costLogsTemplate = useAtomValue(selectedCostLogsTemplate);

  const worklogsTemplateId = useAtomValue(selectedWorklogsTemplateId);
  const costLogsTemplateId = useAtomValue(selectedCostLogsTemplateId);

  const hostLogsTemplate =
    hostLogTemplateType === LogTemplateType.CostLogTemplate ? costLogsTemplate : worklogsTemplate;
  const hostLogsTemplateId =
    hostLogTemplateType === LogTemplateType.CostLogTemplate
      ? costLogsTemplateId
      : worklogsTemplateId;

  const [fields, setFields] = useAtom(
    hostLogTemplateType === LogTemplateType.CostLogTemplate
      ? costLogsTemplateFields
      : worklogsTemplateFields
  );

  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const [logSections, setLogSections] = useState<LogSection[]>(hostLogsTemplate?.sections ?? []);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const templateLabel = HOST_TEMPLATE_DISPLAY[hostLogTemplateType] ?? 'Logs';

  useEffect(() => {
    setLogSections(hostLogsTemplate?.sections ?? []);

    const templateFields = hostLogsTemplate?.fields ?? [];
    setFields(templateFields);
  }, [hostLogsTemplate, setFields]);

  const [updateWorklogsTemplateSections, { loading: isLoading }] = useUpdateLogsTemplateMutation({
    refetchQueries: [AllLogsTemplatesByTypeDocument, LogsTemplateDocument],
    onCompleted: (response) => {
      if (!response?.updateLogsTemplate) {
        showSnackBar(`An error occurred while updating ${templateLabel.toLowerCase()} template`, 'error');
        return;
      }
      showSnackBar(`${templateLabel} template saved successfully`, 'success');
      setMode('ReadOnly');
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
      setMode('ReadOnly');
    },
  });

  const handleCancel = useCallback(() => {
    setLogSections(hostLogsTemplate?.sections ?? []);
    const templateFields = hostLogsTemplate?.fields ?? [];
    setFields(removeTypename(templateFields));

    setMode('ReadOnly');
  }, [hostLogsTemplate, setFields]);

  const handleSaveWorklogsTemplate = useCallback(() => {
    updateWorklogsTemplateSections({
      variables: {
        input: {
          structureId,
          templateId: hostLogsTemplateId ?? '',
          lastModifiedFor: 'Update Log Form Sections',
          sections: removeTypename(logSections),
          fields: removeTypename(fields),
        },
      },
      refetchQueries: [GetVersionsDocument],
    });
  }, [structureId, hostLogsTemplateId, fields, logSections, updateWorklogsTemplateSections]);

  const handleCreateFirstSection = useCallback(() => {
    const firstSection: LogSection = {
      id: generateMongoObjectId(),
      key: '',
      title: 'New Section',
      sectionUnit: SectionUnitsEnum.Hours,
      order: 1,
      defaultOpen: true,
      fieldKeys: [],
    };

    setLogSections([firstSection]);
  }, []);

  if (logSections.length === 0)
    return (
      <Box sx={{ pt: 2 }}>
        <FieldPanel justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
          <EmptyLogSectionsScreen onCreateFirstSection={handleCreateFirstSection} />
        </FieldPanel>
      </Box>
    );

  return (
    <Stack paddingTop={4}>
      <FormHeader>
        <Typography fontSize="2.2rem" fontWeight={500}>
          KPI Configuration
        </Typography>

        {mode === 'ReadOnly' ? (
          <Tooltip
            title="Edit Structure"
            placement="top"
            arrow
            slotProps={{
              popper: { sx: tooltipStyles },
            }}
          >
            <IconButton onClick={() => setMode('Edit')}>
              <Edit
                fontSize="inherit"
                sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
              />
            </IconButton>
          </Tooltip>
        ) : (
          <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
            <Button
              variant="contained"
              sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
              onClick={handleSaveWorklogsTemplate}
              disabled={isLoading}
            >
              {isLoading ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="contained"
              sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </Stack>
        )}
      </FormHeader>

      <LineBreak marginBottom={2} />

      <Box p={3}>
        <Stack spacing={2}>
          <LogSectionsList
            mode={mode}
            logSections={logSections}
            onChangeLogSections={setLogSections}
            hostLogTemplateType={hostLogTemplateType}
          />
        </Stack>
      </Box>
    </Stack>
  );
};
